/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Shopping, 10x — the household-wide API (docs/SHOPPING_LIST_10X.md §6–7).
 *
 * One state snapshot of every list, a feed of every change after it, a live
 * stream of the same, and one write path: a batch of operations, each with
 * its own id so a retry is never applied twice.
 */

import { api, authHeaders, ApiError } from "./client.ts";

export type ItemStatus = "needed" | "got" | "unavailable";

export interface LiveList {
  id: string;
  title: string;
  kind: "home" | "custom" | "template";
  icon: string | null;
  color: string | null;
  pinned: boolean;
  status: string;
  store_id: string | null;
  archived_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  is_stale: boolean;
  covers_from: string | null;
  covers_to: string | null;
}

export interface LiveItem {
  id: string;
  list_id: string;
  food_id: string | null;
  display_name: string;
  quantity: number | null;
  unit: string | null;
  quantity_note: string | null;
  aisle: string | null;
  origin: string;
  status: ItemStatus;
  is_checked: boolean;
  checked_by: string | null;
  checked_at: string | null;
  notes: string | null;
  brand: string | null;
  urgent: boolean;
  position: number | null;
  added_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  is_suppressed?: boolean;
  store_id?: string | null;
}

export interface LiveStore {
  id: string;
  name: string;
  icon: string | null;
  sort_order: number;
  is_default: boolean;
  /** Category keys in this store's walking order; empty = the family's order. */
  aisle_order: string[];
}

export interface LiveClaim {
  list_id: string;
  category_key: string;
  member_sub: string;
  claimed_at: string;
}

export interface LiveSession {
  id: string;
  list_id: string;
  member_sub: string;
  store_id: string | null;
  started_at: string;
  ended_at: string | null;
}

export interface LiveShare {
  id: string;
  list_id: string;
  token: string;
  label: string;
  created_by: string | null;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
}

export interface LiveCategory {
  key: string;
  name: string;
  icon: string | null;
  sort_order: number;
  hidden?: boolean;
}

export interface LiveMember { sub: string; name: string | null }

export interface LiveState {
  lists: LiveList[];
  items: LiveItem[];
  categories: LiveCategory[];
  members: LiveMember[];
  stores: LiveStore[];
  claims: LiveClaim[];
  sessions: LiveSession[];
  shares: LiveShare[];
  me: string;
  latestSeq: number;
}

export interface LiveChange {
  seq: number;
  op: "item.upsert" | "item.delete" | "item.purge" | "list.upsert" | "list.delete" | string;
  listId: string | null;
  itemId: string | null;
  payload: Record<string, unknown>;
  actor: { sub: string | null; name: string | null };
  at: string;
}

export type ShoppingOp = { id: string; op: string; clientTs?: string } & Record<string, unknown>;

export interface OpResult {
  id: string;
  op: string;
  status: "applied" | "duplicate" | "rejected";
  error?: string;
  itemId?: string;
  listId?: string;
  merged?: boolean;
}

export interface ParsedItem {
  name: string;
  quantity: number | null;
  unit: string | null;
  notes: string | null;
  urgent: boolean;
  aisle: string;
  storeId: string | null;
  source: string;
}

export interface Suggestion {
  name: string;
  aisle: string | null;
  quantity: number | null;
  unit: string | null;
  source: "history" | "catalog";
  storeId: string | null;
}

export interface Usual {
  name: string;
  aisle: string | null;
  quantity: number | null;
  unit: string | null;
  storeId: string | null;
  everyDays: number;
  daysSince: number;
}

export interface MergePlanResult {
  listId: string;
  title: string;
  added: number;
  topped: number;
  dates: string[];
  alreadyCovered: string[];
  skippedStaples: string[];
  skippedInStock: string[];
}

export const ShoppingLiveApi = {
  state: () => api.get<LiveState>("/api/shopping/state"),
  changes: (since: number) =>
    api.get<{ changes: LiveChange[]; latestSeq: number; more: boolean }>(
      `/api/shopping/changes?since=${since}`),
  ops: (ops: ShoppingOp[]) =>
    api.post<{ results: OpResult[]; latestSeq: number }>("/api/shopping/ops", { ops }),
  parse: (text: string) => api.post<{ items: ParsedItem[] }>("/api/shopping/parse", { text }),
  suggest: (q: string) =>
    api.get<{ suggestions: Suggestion[] }>(`/api/shopping/suggest?q=${encodeURIComponent(q)}`),
  recent: (listId?: string) =>
    api.get<{ items: Array<{ name: string; aisle: string | null; quantity: number | null; unit: string | null; storeId: string | null }> }>(
      `/api/shopping/recent${listId ? `?listId=${listId}` : ""}`),
  usuals: () => api.get<{ items: Usual[] }>("/api/shopping/usuals"),
  recall: (name: string) =>
    api.get<{ aisle: string | null; storeId: string | null }>(
      `/api/shopping/recall?name=${encodeURIComponent(name)}`),
  mergePlan: (body: { listId: string; dates?: string[]; from?: string; to?: string;
    applyStaples?: boolean; applyInventory?: boolean }) =>
    api.post<MergePlanResult>("/api/shopping/merge-plan", body),
};

/** The public side of a temporary link — no account, the token is the key. */
export interface GuestState {
  me: string;
  label: string;
  expiresAt: string;
  sharedBy: string | null;
  list: { id: string; title: string; icon: string | null };
  items: LiveItem[];
  categories: LiveCategory[];
  latestSeq: number;
}

export async function guestFetch<T>(token: string, path = "", body?: unknown): Promise<T> {
  const res = await fetch(`/api/share/${encodeURIComponent(token)}${path}`, body === undefined
    ? { headers: { Accept: "application/json" } }
    : { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) {
    const msg = parsed && typeof parsed === "object" && "error" in parsed
      ? String((parsed as { error: unknown }).error) : `Request failed (${res.status}).`;
    throw new ApiError(res.status, msg, parsed);
  }
  return parsed as T;
}

/**
 * Follow the household's live stream until `signal` aborts or the connection
 * drops (the promise then settles — the caller decides when to reconnect).
 *
 * EventSource can't send an Authorization header, so this is a fetch whose
 * body is read as it arrives and split on the SSE frame boundary.
 */
export async function followStream(
  since: number,
  handlers: { onReady: () => void; onChange: (c: LiveChange) => void; onClosed?: () => void },
  signal: AbortSignal,
  guestToken?: string,
): Promise<void> {
  const url = guestToken
    ? `/api/share/${encodeURIComponent(guestToken)}/stream?since=${since}`
    : `/api/shopping/stream?since=${since}`;
  const res = await fetch(url, {
    headers: { ...(guestToken ? {} : await authHeaders()), Accept: "text/event-stream" },
    signal,
  });
  if (!res.ok || !res.body) throw new ApiError(res.status, `Live updates unavailable (${res.status}).`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let cut: number;
    while ((cut = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const parsed = parseFrame(frame);
      if (!parsed) continue;
      if (parsed.event === "ready") handlers.onReady();
      else if (parsed.event === "closed") handlers.onClosed?.();
      else if (parsed.event === "change") {
        try { handlers.onChange(JSON.parse(parsed.data) as LiveChange); } catch { /* skip a bad frame */ }
      }
    }
  }
}

/** One SSE frame → its event name and data. Comments (heartbeats) → null. */
export function parseFrame(frame: string): { event: string; data: string } | null {
  let event = "message";
  const data: string[] = [];
  for (const line of frame.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const i = line.indexOf(":");
    const field = i === -1 ? line : line.slice(0, i);
    const value = i === -1 ? "" : line.slice(i + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }
  if (!data.length && event === "message") return null;
  return { event, data: data.join("\n") };
}
